'use client'
import { Button } from '@/components/Button'
import { Checkbox } from '@/components/Checkbox'
import { Popup } from '@/components/Popup'
import { SearchInput } from '@/components/SearchInput'
import { Icon } from '@/icons'
import { MKTU_CLASS_CATEGORY, MKTU_CLASS_CATEGORY_TITLE } from '@/models/Mktu'
import { MktuClassAnnotation } from '@/domains/Account/modules/Mktu/components'
import { MktuClassWord } from '@/services/Api/generated'
import { NiceSwitch, SelectedWordsList } from '@/shared/Mktu/components'
import { ListSelectedWordsPopup } from '@/shared/Mktu/components/ListSelectedWordsPopup'
import { WordList } from '@/shared/Mktu/components/WordList'
import { MktuClassStore } from '@/store/Mktu/MktuClass'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ListViewToggle, TypeOfView } from '../ListViewToggle'
import styles from './MktuClassPopup.module.scss'
import { MktuClassPopupStore } from './MktuClassPopup.store'

enum SelectMode {
  Class = 'CLASS',
  Words = 'WORDS',
}

interface MktuClassPopupProps {
  mktuClass: MktuClassStore
  onClose: () => void
  onBack?: () => void
  onApplyWords?: (ids: MktuClassWord['word'][]) => void
  onSelectMktuClass?: (mktuClass: MktuClassStore) => void
  withBackButton?: boolean
  backButtonText?: string
  showOnlySelectedWords?: boolean
  withSettingsWords?: boolean
  withDescriptionClass?: boolean
  isReadonly?: boolean
  isSelectedClass?: boolean
}
export const MktuClassPopup: FunctionComponent<MktuClassPopupProps> = observer(
  ({
    mktuClass,
    onClose,
    onBack,
    onApplyWords,
    onSelectMktuClass,
    showOnlySelectedWords = false,
    withSettingsWords = true,
    withDescriptionClass = true,
    withBackButton,
    backButtonText = 'Назад',
    isReadonly = false,
    isSelectedClass = false,
  }) => {
    const bodyRef = useRef<HTMLDivElement>(null)
    const wordListRef = useRef<HTMLDivElement>(null)

    const [store] = useState(() => {
      return new MktuClassPopupStore({ mktuClass })
    })

    /* SETTINGS VISIBLE ELEMENTS */
    // isReadonly = false - не переключает слова на ница/не ница
    const withNiceSwitch = useMemo(() => !isReadonly, [isReadonly])

    const selectMode = useMemo(() => {
      if (onSelectMktuClass != null) {
        return SelectMode.Class
      }
      if (onApplyWords != null) {
        return SelectMode.Words
      }
    }, [onSelectMktuClass, onApplyWords])

    const withSelectAll = useMemo(
      () => !isReadonly && selectMode !== SelectMode.Class,
      [isReadonly, selectMode]
    )

    const withCloseButtonFooter = useMemo(() => {
      return isReadonly
    }, [isReadonly])
    /* SETTINGS VISIBLE ELEMENTS */

    const [typeOfView, setTypeOfView] = useState<TypeOfView>(TypeOfView.Grid)
    const [isSelectedWordsPopupVisible, setIsSelectedWordsPopupVisible] =
      useState(false)

    useEffect(() => {
      store.setMktuClass(mktuClass)
      mktuClass.getWords()
    }, [mktuClass, store])

    const apply = useCallback(() => {
      onApplyWords(store.selectedWordsNames)
    }, [onApplyWords, store])

    const selectMktuClass = useCallback(() => {
      if (onSelectMktuClass != null) {
        onSelectMktuClass(mktuClass)
      }
    }, [mktuClass, onSelectMktuClass])

    const showSelectedWordsPopup = useCallback(() => {
      setIsSelectedWordsPopupVisible(true)
    }, [])

    const hideSelectedWordsPopup = useCallback(() => {
      setIsSelectedWordsPopupVisible(false)
    }, [])

    const deleteSelectedWord = useCallback(
      (word: MktuClassWord) => {
        store.selectWord(word.word)
      },
      [store]
    )

    const scrollUp = useCallback(() => {
      if (wordListRef.current == null || bodyRef.current == null) {
        return
      }

      const elements = Array.from(wordListRef.current.children)
      const additionalScrollSize = getHiddenPartElement(
        bodyRef.current,
        elements
      )

      if (additionalScrollSize > 1) {
        bodyRef.current.scrollBy(0, -28 + additionalScrollSize)
      } else {
        bodyRef.current.scrollBy(0, -36)
      }
    }, [])

    const scrollDown = useCallback(() => {
      if (wordListRef.current == null || bodyRef.current == null) {
        return
      }
      const elements = Array.from(wordListRef.current.children)

      const additionalScrollSize = getHiddenPartElement(
        bodyRef.current,
        elements
      )
      if (additionalScrollSize > 1) {
        bodyRef.current.scrollBy(0, additionalScrollSize)
      } else {
        bodyRef.current.scrollBy(0, 36 + additionalScrollSize)
      }
    }, [])

    if (isSelectedWordsPopupVisible) {
      return (
        <ListSelectedWordsPopup
          words={store.selectedWords}
          onDelete={deleteSelectedWord}
          onBack={hideSelectedWordsPopup}
          onClose={onClose}
        />
      )
    }

    return (
      <Popup onClose={onClose} className={cn(styles.popup)}>
        <div className={styles.popupContainer}>
          <div className={styles.header}>
            {withBackButton ? (
              <button
                type="button"
                onClick={onBack}
                className={styles.backButton}
              >
                <Icon name="back" className={styles.iconBack} />{' '}
                {backButtonText}
              </button>
            ) : null}
          </div>
          <div className={styles.wordListControl}>
            <button
              type="button"
              className={styles.wordListControlButton}
              onClick={scrollUp}
            >
              <Icon name="chevronUp" className={styles.wordListControlUp} />
            </button>
            <button
              type="button"
              className={styles.wordListControlButton}
              onClick={scrollDown}
            >
              <Icon name="chevronDown" className={styles.wordListControlUp} />
            </button>
          </div>
          <div className={styles.body} ref={bodyRef}>
            <button onClick={onClose} className={styles.closeButton}>
              <Icon name="close" className={styles.closeButtonIcon} />
            </button>
            <div
              className={cn(styles.infoClass, {
                [styles.withBackground]: withDescriptionClass,
              })}
            >
              <div className={styles.headerDescription}>
                <div className={styles.popupTitle}>
                  Класс {mktuClass.number}
                </div>
                <div className={styles.tag}>
                  {MKTU_CLASS_CATEGORY_TITLE[mktuClass.type]}
                </div>
              </div>
              {withDescriptionClass ? (
                <>
                  <div className={styles.description}>
                    {mktuClass.description}
                  </div>
                  <MktuClassAnnotation
                    explanation={mktuClass.explanation}
                    included={mktuClass.included}
                    excluded={mktuClass.excluded}
                  />
                </>
              ) : null}
            </div>
            {withSettingsWords ? (
              <>
                <div className={styles.row}>
                  <div className={styles.subTitle}>
                    {mktuClass.type === MKTU_CLASS_CATEGORY.PRODUCT
                      ? 'Товары класса, выбранные для регистрации'
                      : 'Услуги класса, выбранные для регистрации'}
                  </div>
                  {withNiceSwitch ? (
                    <NiceSwitch
                      onChange={store.changeOnlyNice}
                      value={store.onlyNice}
                    />
                  ) : null}
                </div>
                <SearchInput
                  className={styles.search}
                  placeholder="Поиск"
                  value={store.search}
                  onChange={store.setSearch}
                />
                <div className={styles.separator}></div>
              </>
            ) : null}
            <div className={styles.controls}>
              {withSelectAll ? (
                <div className={styles.selectAllContainer}>
                  <Checkbox
                    label="Выбрать все"
                    className={styles.selectAll}
                    onChange={store.toggleSelectedAll}
                    value={store.isSelectedAll}
                    disabled={store.search.length !== 0}
                  />
                </div>
              ) : null}
              <ListViewToggle value={typeOfView} onChange={setTypeOfView} />
            </div>
            {selectMode === SelectMode.Words ? (
              <SelectedWordsList
                className={styles.selectedWords}
                words={store.selectedWords}
                onDelete={store.selectWord}
                onShowAll={showSelectedWordsPopup}
              />
            ) : null}
            {!withSelectAll || !store.isSelectedAll ? (
              <WordList
                ref={wordListRef}
                className={styles.words}
                words={
                  showOnlySelectedWords
                    ? store.selectedWordsWithSearch
                    : store.wordsWithFilters
                }
                selectedWordNames={store.selectedWordsNames}
                typeOfView={typeOfView}
                isLoadingWords={mktuClass.loadingWords}
                isReadonly={isReadonly}
                onSelectWord={store.selectWord}
              />
            ) : null}
          </div>
          <div className={styles.footer}>
            {withCloseButtonFooter ? (
              <Button
                className={cn(styles.button)}
                onClick={onClose}
                variant="secondary"
              >
                Закрыть
              </Button>
            ) : null}

            {selectMode === SelectMode.Class ? (
              <Button
                className={styles.button}
                name={isSelectedClass ? 'Отменить выбор' : 'Выбрать класс'}
                onClick={selectMktuClass}
              />
            ) : null}

            {selectMode === SelectMode.Words ? (
              <>
                <Button
                  className={cn(styles.button, styles.buttonReset)}
                  variant="secondary"
                  disabled={store.selectedWordsNames.length === 0}
                  onClick={store.resetAll}
                >
                  <Icon
                    name="trash"
                    className={cn(styles.trashIcon, {
                      [styles.isDisabled]:
                        store.selectedWordsNames.length === 0,
                    })}
                  />
                  Сбросить все
                </Button>
                {store.selectedWordsNames.length !== 0 ? (
                  <span className={styles.selectedCount}>
                    Выбрано позиций в классе: {store.selectedWordsNames.length}
                  </span>
                ) : null}
                <Button
                  className={styles.button}
                  name="Применить"
                  onClick={apply}
                />
              </>
            ) : null}
          </div>
        </div>
      </Popup>
    )
  }
)

// Получаем размер скрытой части последнего элемента
function getHiddenPartElement(container: HTMLDivElement, elements: Element[]) {
  const containerRect = container.getBoundingClientRect()

  const edgeElement = elements.findLast((element) => {
    const elementRect = element.getBoundingClientRect()
    // Элемент считается видимым, если он хотя бы частично в контейнере
    const isVisible =
      elementRect.bottom > containerRect.top &&
      elementRect.top < containerRect.bottom
    return isVisible
  })

  if (edgeElement == null) {
    return 0
  }
  const edgeElementRect = edgeElement.getBoundingClientRect()
  return Math.max(edgeElementRect.bottom - containerRect.bottom, 0)
}
